import { useState } from 'react';

/** Remembers an in-screen tab selection for the browser session.
 *
 *  The console has no URL routing — `screen` and every tab are React state — so
 *  a reload drops the operator back on the first tab. That is tolerable for
 *  navigation but not for the long screens (credit limits, bulk CSV) where a
 *  reload is part of the workflow. sessionStorage is the right scope: the value
 *  is a tab id, not session data, and it should not outlive the tab.
 *
 *  `valid` guards against a stored id that no longer exists after a rename. */
export function useTabState<Id extends string>(
  storageKey: string,
  initial: Id,
  valid: readonly Id[]
): readonly [Id, (next: Id) => void] {
  const [tab, setTab] = useState<Id>(() => {
    const stored = sessionStorage.getItem(storageKey) as Id | null;
    return stored && valid.includes(stored) ? stored : initial;
  });
  return [
    tab,
    (next: Id) => {
      sessionStorage.setItem(storageKey, next);
      setTab(next);
    }
  ] as const;
}
